// src/services/reportService.js
//
// Report service layer
// ============================================================================
// PURPOSE
// ----------------------------------------------------------------------------
// This file is the frontend service wrapper for report-related backend calls.
//
// It should stay intentionally small and predictable:
// - one function per backend report endpoint
// - minimal input normalization
// - consistent logging
// - no UI logic
// - no formatting logic
//
// WHY THIS FILE EXISTS
// ----------------------------------------------------------------------------
// We want React report pages to depend on a clean service API instead of
// building raw Axios requests inline.
//
// This makes the app easier to maintain because:
// - request construction is centralized
// - backend URL changes are localized
// - logging is consistent
// - frontend pages remain focused on rendering / UX
//
// CURRENT ARCHITECTURE ALIGNMENT
// ----------------------------------------------------------------------------
// This project already uses a central apiClient located in:
//
//   ../utils/apiClient
//
// That apiClient is the single owner of:
// - access token handling
// - refresh flow
// - Device-Id / Browser-Id / X-Session-Id headers
// - auth retry behavior
//
// Therefore this service should NOT duplicate auth/session logic.
// It should only call apiClient in a clean way.
//
// IMPORTANT ROUTING CLARIFICATION
// ----------------------------------------------------------------------------
// Frontend page routes and backend API routes are NOT the same thing.
//
// Frontend pages now live under React Router paths like:
//   /reports/income
//   /reports/commission
//   /reports/slot-performance
//
// But backend API endpoints remain:
//   GET /reports/income
//   GET /reports/commission
//   GET /reports/slot-occupancy
//   etc.
//
// This is correct and intentional.
// The page URL tells the browser which React page to render.
// The service URL tells Axios which backend endpoint to call.
//
// IMPORTANT NOTE ABOUT withCredentials
// ----------------------------------------------------------------------------
// Because the backend auth model still uses cookies for refresh/logout and
// possibly some browser-bound flows, we keep `withCredentials: true` explicitly
// here for clarity and compatibility.
//
// IMPORTANT EXTENSION
// ----------------------------------------------------------------------------
// This file now covers three report families:
//
// 1) Established business reports
//    - income
//    - commission
//
// 2) Operational visits-based analytics
//    - slot occupancy / slot performance
//    - weekday economics
//    - idle windows
//    - daily schedule quality
//    - non-completed loss
//
// 3) Commercial / strategic analytics
//    - service profitability
//    - category economics
//    - premium windows
//    - client retention lite
//    - trend & seasonality
//
// SERVICE LAYER POLICY
// ----------------------------------------------------------------------------
// - Keep all network calls here.
// - Normalize input lightly.
// - Log failures in one consistent shape.
// - Throw original Axios error upward so UI pages may decide how to render it.
//

import apiClient from "../utils/apiClient";

/* ============================================================================
   Small shared helpers
============================================================================ */


/**
 * Check whether a value looks like a valid YYYY-MM month string.
 */
function isIsoMonth(value) {
  return typeof value === "string" && /^\d{4}-\d{2}$/.test(value.trim());
}

/**
 * Normalize a YYYY-MM month input for monthly reports.
 */
function normalizeMonthParam(value) {
  if (!isIsoMonth(value)) return undefined;
  return value.trim();
}

/**
 * Check whether a value looks like a valid YYYY-MM-DD date string.
 *
 * We keep validation intentionally lightweight:
 * - enough to avoid obviously broken requests
 * - not meant to replace backend validation
 */
function isIsoDateOnly(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * Normalize a date input.
 *
 * Returns:
 * - trimmed YYYY-MM-DD string when valid
 * - undefined when empty / invalid
 *
 * Why undefined?
 * - Axios omits undefined params
 * - this keeps request construction simple and clean
 */
function normalizeDateParam(value) {
  if (!isIsoDateOnly(value)) return undefined;
  return value.trim();
}

/**
 * Normalize an array of user IDs for query params.
 *
 * Accepted input examples:
 * - [1, 2, 3]
 * - ["1", "2", "3"]
 * - []
 * - undefined
 *
 * Output:
 * - array of finite numbers
 *
 * Why normalize here?
 * - pages should not worry about transport-level cleanup
 * - backend receives a cleaner query shape
 */
function normalizeUserIds(userIds) {
  if (!Array.isArray(userIds)) return [];

  return userIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
}

/**
 * Shared structured error logger for report requests.
 *
 * We log:
 * - human-friendly scope label
 * - normalized params
 * - backend status/data when available
 *
 * This keeps console output readable while still preserving debugging detail.
 */
function logReportError(scope, error, extra = {}) {
  console.error(`[ReportService] ❌ ${scope} failed.`, {
    ...extra,
    message: error?.message,
    status: error?.response?.status,
    data: error?.response?.data,
    error,
  });
}

/**
 * Shared helper for most date-based reports.
 *
 * Common report transport contract:
 * - from
 * - to
 * - userIds
 */
function buildDateUserParams(from, to, userIds) {
  return {
    from: normalizeDateParam(from),
    to: normalizeDateParam(to),
    userIds: normalizeUserIds(userIds),
  };
}

/**
 * Shared GET wrapper for report endpoints.
 *
 * Why centralize this?
 * - keeps individual report methods short
 * - ensures all report calls use the same request style
 * - makes it easier to change common behavior later
 */
async function getReport(url, params, scope) {
  try {
    const response = await apiClient.get(url, {
      params,
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    logReportError(scope, error, params);
    throw error;
  }
}

/* ============================================================================
   Established business reports
============================================================================ */

/**
 * Fetch income report data.
 *
 * Backend contract:
 *   GET /reports/income
 *
 * Query params:
 *   - from: YYYY-MM-DD
 *   - to:   YYYY-MM-DD
 *
 * Page route typically using this service:
 *   /reports/income
 */
export const fetchIncomeReport = async (from, to) => {
  const params = {
    from: normalizeDateParam(from),
    to: normalizeDateParam(to),
  };

  return getReport("/reports/income", params, "fetchIncomeReport");
};

/**
 * Fetch commission report data.
 *
 * Backend contract:
 *   GET /reports/commission
 *
 * Query params:
 *   - from: YYYY-MM-DD
 *   - to: YYYY-MM-DD
 *   - userIds: array of selected user IDs
 *
 * Page route typically using this service:
 *   /reports/commission
 */
export const fetchCommissionReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport("/reports/commission", params, "fetchCommissionReport");
};

/* ============================================================================
   Operational visits-based analytics
============================================================================ */

/**
 * Fetch slot occupancy / slot performance report.
 *
 * Backend contract:
 *   GET /reports/slot-occupancy
 *
 * Page route typically using this service:
 *   /reports/slot-performance
 */
export const fetchSlotOccupancyReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport("/reports/slot-occupancy", params, "fetchSlotOccupancyReport");
};

/**
 * Fetch weekday economics report.
 *
 * Backend contract:
 *   GET /reports/weekday-economics
 *
 * Page route:
 *   /reports/weekday-economics
 */
export const fetchWeekdayEconomicsReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/weekday-economics",
    params,
    "fetchWeekdayEconomicsReport"
  );
};

/**
 * Fetch idle windows report.
 *
 * Backend contract:
 *   GET /reports/idle-windows
 *
 * Page route:
 *   /reports/idle-windows
 */
export const fetchIdleWindowsReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport("/reports/idle-windows", params, "fetchIdleWindowsReport");
};

/**
 * Fetch daily schedule quality report.
 *
 * Backend contract:
 *   GET /reports/daily-schedule-quality
 *
 * Page route:
 *   /reports/daily-schedule-quality
 */
export const fetchDailyScheduleQualityReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/daily-schedule-quality",
    params,
    "fetchDailyScheduleQualityReport"
  );
};

/**
 * Fetch non-completed loss report.
 *
 * Backend contract:
 *   GET /reports/non-completed-loss
 *
 * Page route:
 *   /reports/non-completed-loss
 */
export const fetchNoShowLossReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/no-show-loss",
    params,
    "fetchNoShowLossReport"
  );
};

/* ============================================================================
   Economics / profitability analytics
============================================================================ */

/**
 * Fetch service profitability report.
 *
 * Backend contract:
 *   GET /reports/service-profitability
 *
 * Page route:
 *   /reports/service-profitability
 */
export const fetchServiceProfitabilityReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/service-profitability",
    params,
    "fetchServiceProfitabilityReport"
  );
};

/**
 * Fetch category economics report.
 *
 * Backend contract:
 *   GET /reports/category-economics
 *
 * Page route:
 *   /reports/category-economics
 */
export const fetchCategoryEconomicsReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/category-economics",
    params,
    "fetchCategoryEconomicsReport"
  );
};

/**
 * Fetch premium windows report.
 *
 * Backend contract:
 *   GET /reports/premium-windows
 *
 * Page route:
 *   /reports/premium-windows
 */
export const fetchPremiumWindowReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/premium-windows",
    params,
    "fetchPremiumWindowReport"
  );
};

/* ============================================================================
   Strategic / CRM-lite / trend analytics
============================================================================ */

/**
 * Fetch client retention lite report.
 *
 * Backend contract:
 *   GET /reports/client-retention-lite
 *
 * Page route:
 *   /reports/client-retention-lite
 */
export const fetchClientRetentionLiteReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);
  return getReport(
    "/reports/client-retention-lite",
    params,
    "fetchClientRetentionLiteReport"
  );
};

/**
 * Fetch trend & seasonality report.
 *
 * Backend contract:
 *   GET /reports/trend-seasonality
 *
 * Query params:
 *   - from
 *   - to
 *   - userIds
 *   - granularity: "week" | "month"
 *
 * Page route:
 *   /reports/trend-seasonality
 */
export const fetchTrendSeasonalityReport = async (
  from,
  to,
  userIds,
  granularity = "month"
) => {
  const params = {
    ...buildDateUserParams(from, to, userIds),
    granularity: String(granularity || "month").trim().toLowerCase(),
  };

  return getReport(
    "/reports/trend-seasonality",
    params,
    "fetchTrendSeasonalityReport"
  );
};


/* ============================================================================
   HR reports
============================================================================ */

/**
 * Fetch HR absence payroll report data.
 *
 * Backend contract:
 *   GET /reports/hr/absence-payroll
 *
 * Query params:
 *   - from: YYYY-MM-DD
 *   - to: YYYY-MM-DD
 *   - userIds: optional array of selected employee IDs
 *
 * Page route:
 *   /reports/hr/absence-payroll
 *
 * Important architectural boundary:
 * - this is a read-only reporting endpoint,
 * - it consumes existing HR/leave snapshots and day rows,
 * - it must not alter leave request creation, approval, cancellation, balance
 *   calculation or time-settlement behavior.
 */
export const fetchHrAbsencePayrollReport = async (from, to, userIds) => {
  const params = buildDateUserParams(from, to, userIds);

  return getReport(
    "/reports/hr/absence-payroll",
    params,
    "fetchHrAbsencePayrollReport"
  );
};

/* ============================================================================
   Default export
============================================================================ */

export default {
  fetchIncomeReport,
  fetchCommissionReport,

  fetchSlotOccupancyReport,
  fetchServiceProfitabilityReport,
  fetchCategoryEconomicsReport,
  fetchWeekdayEconomicsReport,
  fetchIdleWindowsReport,
  fetchDailyScheduleQualityReport,
  fetchNoShowLossReport,
  fetchClientRetentionLiteReport,
  fetchPremiumWindowReport,
  fetchTrendSeasonalityReport,
  fetchHrAbsencePayrollReport,
};